// Get the productType for the pricing API calls (for the pricing-items and discounts).
// For example for gift card productType, we want the classic pricing-items and
// discounts - because the gift card applied on the classic product
import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import { NEW_PRICING_SUPPORTED_PRODUCT_TYPES } from '../../components/Pricing/PricingUtil'
import { type PricingItem } from '../../components/Pricing/types/pricingItem.types'
import {
  hashTilePricingProduct,
  type TILE_SIZES,
  type WallFramePresetConfig,
  type WallFramePresetGroup,
} from '@mixtiles/web-backend-shared'
import {
  type PriceDifferenceMap,
  type PricingData,
} from '../../components/Pricing/types/PricingProvider.types'
import { formatPrice } from 'utils/utils'

export const getPricingProductType = (productType: PRODUCT_TYPES) => {
  if (productType === PRODUCT_TYPES.GIFT_CARD) {
    return PRODUCT_TYPES.CLASSIC
  } else {
    return productType
  }
}

export const CART_DISCOUNT_PRODUCT_TYPES = [
  PRODUCT_TYPES.CLASSIC,
  PRODUCT_TYPES.PHOTO_BOOK,
]

export const getDiscountProductTypes = (
  productType: PRODUCT_TYPES
): PRODUCT_TYPES[] => {
  const pricingProductType = getPricingProductType(productType)
  return CART_DISCOUNT_PRODUCT_TYPES.includes(pricingProductType)
    ? CART_DISCOUNT_PRODUCT_TYPES
    : [pricingProductType]
}

export const shouldUseAPI = (productType: PRODUCT_TYPES) =>
  NEW_PRICING_SUPPORTED_PRODUCT_TYPES.includes(productType)

export function formatPricingItems(
  pricingItems: PricingItem[] | undefined
): Record<string, PricingItem> {
  return Object.fromEntries(
    pricingItems?.map((item) => [
      hashTilePricingProduct(item.pricingProduct),
      {
        ...item,
      },
    ]) || []
  )
}

export const getFramePriceDiff = (
  { materialType, frameColor }: WallFramePresetConfig,
  shouldRound: boolean,
  priceDifferenceMap: PriceDifferenceMap,
  pricingData: PricingData
) => {
  if (!priceDifferenceMap) {
    return '0'
  }

  return formatPrice(
    priceDifferenceMap[materialType][frameColor],
    pricingData.currency,
    false,
    shouldRound
  )
}

export const getPresetPriceDiff = (
  preset: WallFramePresetGroup,
  priceDifferenceMap: PriceDifferenceMap,
  pricingData: PricingData,
  frameSizes: TILE_SIZES[]
) => {
  if (!priceDifferenceMap) {
    return '0'
  }

  if (preset.configs.length === 0) {
    return '0'
  }

  let totalValue = 0
  const numFrames = frameSizes.length
  const configs = preset.configs

  // Calculate how many frames each config will be applied to
  const baseCount = Math.floor(numFrames / configs.length)
  const remainder = numFrames % configs.length

  configs.forEach((config, index) => {
    // Add extra frame to first 'remainder' configs
    const framesForThisConfig = baseCount + (index < remainder ? 1 : 0)
    const priceDiff = getFramePriceDiff(
      config,
      false,
      priceDifferenceMap,
      pricingData
    )
    const value = parseFloat(priceDiff.replace(/[^0-9.-]+/g, ''))
    totalValue += value * framesForThisConfig
  })

  if (totalValue === 0) {
    return '0'
  }

  const formattedPrice = getFramePriceDiff(
    configs[0],
    false,
    priceDifferenceMap,
    pricingData
  )
  const numericPart = formattedPrice.replace(/[^0-9.-]+/g, '')

  return formattedPrice.replace(numericPart, totalValue.toString())
}
